import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, isSameDay } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { BusinessHours } from '../types/businessHours';
import { CreateBookingSlot, UpdateBookingSlot } from '../types/bookingSlot';
import { BarberServices } from '../types/barber';
import { generateUUID } from '../utils/uuid';
import { roleContext } from '../context/RoleContext';
import { UserRole } from '../types/role';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import {
  addShopSlot,
  addBarberSlot,
} from '../middleware/reservation_service/createRequest';
import {
  updateShopSlot,
  updateBarberSlot,
} from '../middleware/reservation_service/updateRequest';
import {
  deleteShopSlot,
  deleteBarberSlot,
} from '../middleware/reservation_service/deleteRequest';
import {
  getShopSlotsByShopIdAndBarberId,
  getBarberSlotsByBarberId,
} from '../middleware/reservation_service/getRequests';
import {
  getBarberById,
  getShopBarberById,
  getShopOwnerById,
} from '../middleware/registration_service/getRequests';
import { AxiosResponse } from 'axios';
import { formatTimeToHHMM } from '../utils/dateFormatter';
import Container from '@mui/material/Container';
import SubscriptionRestricted from './components/SubscriptionRestricted';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import AddServices from './components/AddServices';

const ScheduleManager: React.FC = () => {
  const partnerItem = localStorage.getItem('reg_partner_id');
  const partnerId = BigInt(partnerItem ? partnerItem : 0);
  const role = useContext(roleContext);
  const shopItem = localStorage.getItem('reg_shop_id');
  const shopId = BigInt(shopItem ? shopItem : 0);
  const { loading: subscriptionLoading, isActive: hasActiveSubscription } =
    useSubscriptionAccess(role);
  const [shopOwner, setShopOwner] = useState<any>(null);
  const [selectedBarberId, setSelectedBarberId] = useState<bigint | null>(null);
  const [shopBarbers, setShopBarbers] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bookingSlots, setBookingSlots] = useState<CreateBookingSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [barberServices, setBarberServices] = useState<BarberServices[]>([]);
  const [serviceList, setServiceList] = useState<BarberServices[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);
  const [formData, setFormData] = useState({
    service: '',
    startTime: '',
  });
  const [editingSlot, setEditingSlot] = useState<CreateBookingSlot | null>(
    null
  );
  // Multiple start-time selections for bulk slot creation (create mode only)
  const [multiStartTimes, setMultiStartTimes] = useState<string[]>([]);
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'warning' | 'error';
  } | null>(null);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [copySourceDate, setCopySourceDate] = useState<Date | null>(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [isAddServiceDialogOpen, setIsAddServiceDialogOpen] = useState(false);

  // The barber whose services/slots are currently being managed: the
  // logged-in barber themselves, or (for shop owners) the selected shop
  // barber.
  const activeBarberId =
    role === UserRole.SHOP_OWNER ? selectedBarberId : partnerId;
  // Only barbers/shop barbers may add a service to their own profile from
  // here; shop owners must not create services on behalf of another barber.
  const canAddOwnService =
    role === UserRole.BARBER || role === UserRole.SHOP_BARBER;

  const loadShopOwner = async () => {
    try {
      if (role === UserRole.SHOP_OWNER) {
        const response = await getShopOwnerById(partnerId);
        if (response.data) {
          setShopOwner(response.data);
        }
      }
    } catch (error) {
      console.error('Failed to load shop owner data:', error);
    }
  };

  const loadBarberData = async (barberId: bigint) => {
    try {
      setLoading(true);
      let response: AxiosResponse;

      if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
        response = await getShopBarberById(barberId);
      } else {
        response = await getBarberById(barberId);
      }

      if (response.data) {
        if (response.data.businessHours?.businessHoursMap) {
          const newBusinessHours = Object.entries(
            response.data.businessHours.businessHoursMap
          ).map(([day, hours]) => {
            const typedHours = hours as BusinessHours;
            return {
              day,
              startTime: typedHours.startTime,
              endTime: typedHours.endTime,
              open: typedHours.open,
            };
          });
          setBusinessHours(newBusinessHours);
        }

        if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
          if (response.data.barberShopServices) {
            setBarberServices(response.data.barberShopServices);
          }
        } else if (role === UserRole.BARBER) {
          if (response.data.barberServices) {
            setBarberServices(response.data.barberServices);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load barber data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSlots = async () => {
    if (role !== UserRole.BARBER && !shopId) return;

    try {
      setLoading(true);
      let response: AxiosResponse;

      if (role === UserRole.BARBER) {
        response = await getBarberSlotsByBarberId(partnerId);
      } else if (role === UserRole.SHOP_OWNER && selectedBarberId) {
        response = await getShopSlotsByShopIdAndBarberId(
          shopId,
          selectedBarberId
        );
      } else {
        response = await getShopSlotsByShopIdAndBarberId(shopId, partnerId);
      }

      const slots = Array.isArray(response.data) ? response.data : [];

      const formattedSlots: CreateBookingSlot[] = slots.map((slot: any) => ({
        id: slot.id,
        barber_id: BigInt(slot.barber_id),
        service_id: BigInt(slot.service_id),
        start_time: new Date(`${slot.slot_date}T${slot.start_time}:00`),
        status: slot.status,
        slot_date: new Date(slot.slot_date),
        ...(slot.shop_id && { shopId: BigInt(slot.shop_id) }),
      }));

      setBookingSlots(formattedSlots);
    } catch (error) {
      console.error('Failed to load shop/barber slots:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load slots when role, shopId, partnerId, or selectedBarberId changes.
  // Skipped entirely until the partner's subscription access is confirmed,
  // so no reservation data is fetched while access is restricted.
  useEffect(() => {
    if (subscriptionLoading || !hasActiveSubscription) return;

    if (role === UserRole.BARBER) {
      loadSlots();
      return;
    }

    if (
      (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) &&
      shopId
    ) {
      loadSlots();
    }
  }, [
    role,
    shopId,
    partnerId,
    selectedBarberId,
    subscriptionLoading,
    hasActiveSubscription,
  ]);

  // Load shop owner data if role is SHOP_OWNER
  useEffect(() => {
    if (subscriptionLoading || !hasActiveSubscription) return;

    if (role === UserRole.SHOP_OWNER) {
      loadShopOwner();
    }
  }, [role, subscriptionLoading, hasActiveSubscription]);

  // Set initial barber selection for shop owners
  useEffect(() => {
    if (role === UserRole.SHOP_OWNER && shopOwner?.shops?.[0]?.shopBarbers) {
      setShopBarbers(shopOwner.shops[0].shopBarbers);
      if (shopOwner.shops[0].shopBarbers.length > 0) {
        setSelectedBarberId(
          BigInt(shopOwner.shops[0].shopBarbers[0].id) || null
        );
      }
    }
  }, [role, shopOwner]);

  // Load barber data when selectedBarberId or partnerId changes
  useEffect(() => {
    if (subscriptionLoading || !hasActiveSubscription) return;

    if (role === UserRole.SHOP_OWNER && selectedBarberId) {
      loadBarberData(selectedBarberId);
    } else if (role === UserRole.SHOP_BARBER && partnerId) {
      loadBarberData(partnerId);
    } else if (role === UserRole.BARBER && partnerId) {
      loadBarberData(partnerId);
    }
  }, [
    selectedBarberId,
    partnerId,
    role,
    subscriptionLoading,
    hasActiveSubscription,
  ]);

  // Update serviceList when services change
  useEffect(() => {
    const updateServiceList = () => {
      switch (role) {
        case UserRole.BARBER:
          setServiceList(barberServices);
          break;
        case UserRole.SHOP_BARBER:
          setServiceList(barberServices);
          break;
        case UserRole.SHOP_OWNER:
          if (selectedBarberId && barberServices.length > 0) {
            setServiceList(barberServices);
          } else {
            setServiceList([]);
          }
          break;
        default:
          setServiceList([]);
      }
    };

    updateServiceList();
  }, [role, barberServices, selectedBarberId]);

  const handleDateSelect = (value: Date | null) => {
    if (!value) return;
    setSelectedDate(value);
    // Open dialog for past/current/future dates
    setIsDialogOpen(true);
    setEditingSlot(null);
    setFormData({ service: '', startTime: '' });
    setMultiStartTimes([]);
  };

  const handleSaveBookingSlot = async () => {
    if (!selectedDate || !formData.service) return;
    if (!editingSlot && multiStartTimes.length === 0) return;
    if (editingSlot && !formData.startTime) return;

    const barberId =
      role === UserRole.SHOP_OWNER ? selectedBarberId : partnerId;

    if (!barberId) {
      alert('Please select a barber.');
      return;
    }

    const slotDate = new Date(`${format(selectedDate, 'yyyy-MM-dd')}`);
    const serviceId =
      serviceList.find((s) => s.name === formData.service)?.id || BigInt(0);

    try {
      setLoading(true);

      if (editingSlot) {
        const startTime = new Date(
          `${format(selectedDate, 'yyyy-MM-dd')}T${formData.startTime}`
        );

        if (role === UserRole.BARBER) {
          const updateData: UpdateBookingSlot = {
            slot_date: slotDate,
            start_time: startTime,
            service_id: serviceId,
            barber_id: barberId,
            status: 'AVAILABLE',
          };
          await updateBarberSlot(editingSlot.id, updateData);
        } else {
          const updateData: UpdateBookingSlot = {
            shop_id: shopId,
            slot_date: slotDate,
            start_time: startTime,
            service_id: serviceId,
            barber_id: barberId,
            status: 'AVAILABLE',
          };
          await updateShopSlot(editingSlot.id, updateData);
        }

        await Promise.all([loadSlots(), loadBarberData(barberId)]);
        setIsDialogOpen(false);
        setFormData({ service: '', startTime: '' });
        setEditingSlot(null);
        setNotification({
          message: 'Booking slot updated successfully.',
          severity: 'success',
        });
      } else {
        // Bulk creation: issue one concurrent request per selected start time.
        const buildPayload = (time: string): CreateBookingSlot => ({
          id: generateUUID(),
          ...(role !== UserRole.BARBER && { shop_id: shopId }),
          slot_date: slotDate,
          start_time: new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${time}`),
          service_id: serviceId,
          barber_id: barberId,
          status: 'AVAILABLE',
        });

        const results = await Promise.allSettled(
          multiStartTimes.map((time) =>
            role === UserRole.BARBER
              ? addBarberSlot(buildPayload(time))
              : addShopSlot(buildPayload(time))
          )
        );

        const succeeded = results.filter(
          (r) => r.status === 'fulfilled'
        ).length;
        const failed = results.length - succeeded;

        await Promise.all([loadSlots(), loadBarberData(barberId)]);

        if (failed === 0) {
          setIsDialogOpen(false);
          setFormData({ service: '', startTime: '' });
          setMultiStartTimes([]);
          setEditingSlot(null);
          setNotification({
            message: `${succeeded} booking slot${
              succeeded === 1 ? '' : 's'
            } created successfully.`,
            severity: 'success',
          });
        } else {
          setNotification({
            message: `${succeeded} slot${
              succeeded === 1 ? '' : 's'
            } created, ${failed} failed. Please try again for the failed time${
              failed === 1 ? '' : 's'
            }.`,
            severity: succeeded > 0 ? 'warning' : 'error',
          });
        }
      }
    } catch (error) {
      console.error('Failed to save booking slot:', error);
      alert('Failed to save booking slot. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Returns the hourly start-time options for a date that are not already
  // booked for the currently selected service, so bulk creation won't
  // attempt to create duplicate slots.
  const getAvailableStartTimesForDate = (
    date: Date,
    serviceName: string
  ): string[] => {
    const allOptions = getTimeOptions(date);
    const serviceId = serviceList.find((s) => s.name === serviceName)?.id;
    if (!serviceId) return allOptions;

    const takenTimes = new Set(
      getSlotsForDate(date)
        .filter((slot) => BigInt(slot.service_id) === BigInt(serviceId))
        .map((slot) => format(slot.start_time, 'HH:mm'))
    );

    return allOptions.filter((time) => !takenTimes.has(time));
  };

  const handleCopySlots = async () => {
    if (!selectedDate || !copySourceDate) return;

    const barberId =
      role === UserRole.SHOP_OWNER ? selectedBarberId : partnerId;

    if (!barberId) {
      alert('Please select a barber.');
      return;
    }

    const sourceSlots = getSlotsForDate(copySourceDate).filter(
      (slot) => slot.status !== 'CANCELLED'
    );

    if (sourceSlots.length === 0) {
      setNotification({
        message: 'No active slots found on the selected source date.',
        severity: 'warning',
      });
      return;
    }

    const existingTargetCombos = new Set(
      getSlotsForDate(selectedDate).map(
        (slot) =>
          `${slot.service_id.toString()}-${format(slot.start_time, 'HH:mm')}`
      )
    );

    const targetTimeOptions = new Set(getTimeOptions(selectedDate));

    const dedupedSlots = sourceSlots.filter(
      (slot) =>
        !existingTargetCombos.has(
          `${slot.service_id.toString()}-${format(slot.start_time, 'HH:mm')}`
        )
    );

    const slotsToCopy = dedupedSlots.filter((slot) =>
      targetTimeOptions.has(format(slot.start_time, 'HH:mm'))
    );
    const outOfHoursCount = dedupedSlots.length - slotsToCopy.length;

    if (slotsToCopy.length === 0) {
      setNotification({
        message:
          outOfHoursCount > 0
            ? `All remaining source slots fall outside the target date's business hours and were skipped.`
            : 'All slots from the source date already exist on the target date.',
        severity: 'warning',
      });
      return;
    }

    try {
      setCopyLoading(true);

      const slotDate = new Date(`${format(selectedDate, 'yyyy-MM-dd')}`);
      const buildPayload = (slot: CreateBookingSlot): CreateBookingSlot => ({
        id: generateUUID(),
        ...(role !== UserRole.BARBER && { shop_id: shopId }),
        slot_date: slotDate,
        start_time: new Date(
          `${format(selectedDate, 'yyyy-MM-dd')}T${format(
            slot.start_time,
            'HH:mm'
          )}`
        ),
        service_id: slot.service_id,
        barber_id: barberId,
        status: 'AVAILABLE',
      });

      const results = await Promise.allSettled(
        slotsToCopy.map((slot) =>
          role === UserRole.BARBER
            ? addBarberSlot(buildPayload(slot))
            : addShopSlot(buildPayload(slot))
        )
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      const skippedSuffix =
        outOfHoursCount > 0
          ? ` ${outOfHoursCount} slot${
              outOfHoursCount === 1 ? '' : 's'
            } outside the target date's business hours ${
              outOfHoursCount === 1 ? 'was' : 'were'
            } skipped.`
          : '';

      await Promise.all([loadSlots(), loadBarberData(barberId)]);

      if (failed === 0) {
        setIsCopyDialogOpen(false);
        setCopySourceDate(null);
        setNotification({
          message: `${succeeded} slot${
            succeeded === 1 ? '' : 's'
          } copied successfully.${skippedSuffix}`,
          severity: outOfHoursCount > 0 ? 'warning' : 'success',
        });
      } else {
        setNotification({
          message: `${succeeded} slot${
            succeeded === 1 ? '' : 's'
          } copied, ${failed} failed.${skippedSuffix}`,
          severity: succeeded > 0 ? 'warning' : 'error',
        });
      }
    } catch (error) {
      console.error('Failed to copy booking slots:', error);
      setNotification({
        message: 'Failed to copy booking slots. Please try again.',
        severity: 'error',
      });
    } finally {
      setCopyLoading(false);
    }
  };

  const handleEditSlot = (slot: CreateBookingSlot) => {
    setEditingSlot(slot);
    setSelectedDate(slot.start_time);

    const serviceObj = serviceList.find(
      (s) => BigInt(s.id || 0) === BigInt(slot.service_id)
    );

    setFormData({
      service: serviceObj?.name || '',
      startTime: format(slot.start_time, 'HH:mm'),
    });
    setMultiStartTimes([]);

    setIsDialogOpen(true);
  };

  const handleDeleteSlot = async (slotId: string) => {
    try {
      setLoading(true);

      if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
        await deleteShopSlot(slotId);
      } else if (role === UserRole.BARBER) {
        await deleteBarberSlot(slotId);
      }

      setBookingSlots((prev) => prev.filter((slot) => slot.id !== slotId));
    } catch (error) {
      console.error('Failed to delete booking slot:', error);
      alert('Failed to delete booking slot. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getBusinessHoursForDate = (date: Date) => {
    const dayName = format(date, 'EEEE');
    return businessHours?.find((bh) => bh.day === dayName);
  };

  const getTimeOptions = (date: Date): string[] => {
    const dayHours = getBusinessHoursForDate(date);
    if (!dayHours || !dayHours.open) return [];
    const [startH, startM = 0] = dayHours.startTime
      .split(':')
      .map((v) => parseInt(v, 10));
    const [endHRaw, endMRaw = 0] = dayHours.endTime
      .split(':')
      .map((v) => parseInt(v, 10));

    // Overnight hours (e.g. "09:00" - "01:00" the next day) span past
    // midnight. Generate slots through the end of the day, since the
    // post-midnight portion belongs to the following day's own schedule.
    const startMinutes = startH * 60 + startM;
    const endMinutesRaw = endHRaw * 60 + endMRaw;
    const isOvernight = endMinutesRaw <= startMinutes;
    const endH = isOvernight ? 24 : endHRaw;
    const lastHour = isOvernight ? 23 : endH;

    const options: string[] = [];
    for (let hour = startH; hour <= lastHour; hour++) {
      const h = hour.toString().padStart(2, '0');
      options.push(`${h}:00`);
      if (hour < endH) options.push(`${h}:30`);
    }
    return options;
  };

  const getSlotsForDate = (date: Date) => {
    const filteredSlots = bookingSlots.filter((slot) =>
      isSameDay(slot.slot_date, date)
    );

    // For shop owners, only show slots for the selected barber
    if (role === UserRole.SHOP_OWNER && selectedBarberId) {
      return filteredSlots.filter(
        (slot) => slot.barber_id === selectedBarberId
      );
    }

    return filteredSlots;
  };

  const getServiceNameById = (serviceId: bigint) => {
    const service = serviceList.find((s) => BigInt(s.id || 0) === serviceId);
    return service ? service.name : 'Unknown Service';
  };

  const hasBookingSlots = (date: Date) => getSlotsForDate(date).length > 0;

  const DayWithBookings = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, onDaySelect } = props;
    const isBooked = !outsideCurrentMonth && hasBookingSlots(day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastDate = day < today;

    return (
      <PickersDay
        isFirstVisibleCell={true}
        isLastVisibleCell={true}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(isBooked && {
            backgroundColor: 'rgba(215, 167, 72, 0.5)',
            '&:hover': { backgroundColor: 'rgba(215, 167, 72, 0.5)' },
          }),
          ...(isPastDate &&
            isBooked && {
              cursor: 'pointer',
              backgroundColor: 'rgba(215, 167, 72, 0.3)',
              '&:hover': { backgroundColor: 'rgba(215, 167, 72, 0.5)' },
            }),
        }}
        onDaySelect={onDaySelect}
      />
    );
  };

  const selectedDateSlots = selectedDate ? getSlotsForDate(selectedDate) : [];
  const selectedDateHours = selectedDate
    ? getBusinessHoursForDate(selectedDate)
    : null;

  const canSave =
    !!selectedDate &&
    !!formData.service &&
    (editingSlot ? !!formData.startTime : multiStartTimes.length > 0) &&
    (!!selectedDateHours?.open || false) &&
    !loading;

  const copySourceDateHours = copySourceDate
    ? getBusinessHoursForDate(copySourceDate)
    : null;
  const copySourceSlotCount = copySourceDate
    ? getSlotsForDate(copySourceDate).filter((s) => s.status !== 'CANCELLED')
        .length
    : 0;

  if (!subscriptionLoading && !hasActiveSubscription) {
    return <SubscriptionRestricted featureName="Schedule Manager" />;
  }

  return (
    <Container
      sx={{
        marginTop: '75px !important',
        width: '100%',
        padding: '0px 15px !important',
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header Banner */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: 2,
            borderLeft: '5px solid #dd8709',
            color: 'white',
          }}
        >
          <Typography variant="h4" sx={{ fontFamily: 'GFS Didot' }}>
            Schedule Manager {loading && '(Loading...)'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Manage booking slots and availability
          </Typography>
        </Box>

        {role === UserRole.SHOP_OWNER && (
          <Box sx={{ mb: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Select Barber</InputLabel>
              <Select
                value={selectedBarberId?.toString() || ''}
                onChange={(e) =>
                  setSelectedBarberId(BigInt(e.target.value as string) || null)
                }
                label="Select Barber"
                disabled={loading}
              >
                {shopBarbers.map((barber) => (
                  <MenuItem
                    key={barber.id.toString()}
                    value={barber.id.toString()}
                  >
                    {barber.firstName} {barber.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 1, sm: 2 } }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateSelect}
                  slots={{ day: DayWithBookings }}
                  disabled={loading}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                    '& .MuiPickersCalendarHeader-root': {
                      pl: 0.5,
                      pr: 0,
                    },
                    '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer':
                      {
                        justifyContent: 'space-around',
                      },
                    '& .MuiPickersDay-root': {
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            {selectedDate && (
              <Paper sx={{ p: { xs: 1, sm: 2 } }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {format(selectedDate, 'MMMM d, yyyy')}
                </Typography>

                {selectedDateHours && !selectedDateHours.open && (
                  <Typography color="text.secondary">
                    Business is closed on this day
                  </Typography>
                )}

                {selectedDateHours && selectedDateHours.open && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Business Hours:{' '}
                    {formatTimeToHHMM(selectedDateHours.startTime)} -{' '}
                    {formatTimeToHHMM(selectedDateHours.endTime)}
                  </Typography>
                )}

                {selectedDateHours?.open && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                    disabled={loading}
                    onClick={() => {
                      setCopySourceDate(null);
                      setIsCopyDialogOpen(true);
                    }}
                  >
                    Copy Slots From Another Date
                  </Button>
                )}

                {selectedDateSlots.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Booking Slots:
                    </Typography>
                    {selectedDateSlots.map((slot) => (
                      <Box
                        key={slot.id}
                        sx={{
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={`${getServiceNameById(
                            slot.service_id
                          )} (${slot.start_time
                            .toLocaleTimeString()
                            .slice(0, 5)})`}
                          variant="outlined"
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleEditSlot(slot)}
                          disabled={loading || slot.status === 'BOOKED'}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSlot(slot.id)}
                          disabled={loading || slot.status === 'BOOKED'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingSlot ? 'Edit Booking Slot' : 'Create Booking Slot'}
            {selectedDate && ` - ${format(selectedDate, 'MMMM d, yyyy')}`}
          </DialogTitle>
          <DialogContent>
            {selectedDate && selectedDateHours && !selectedDateHours.open ? (
              <Typography color="error">
                Cannot create booking slots \- business is closed on this day
              </Typography>
            ) : (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <TextField
                    select
                    label="Service"
                    value={formData.service}
                    onChange={(e) => {
                      setFormData({ ...formData, service: e.target.value });
                      setMultiStartTimes([]);
                    }}
                    fullWidth
                    disabled={loading || serviceList.length === 0}
                    helperText={
                      serviceList.length === 0 && !canAddOwnService
                        ? 'This barber has not added any services to their profile.'
                        : undefined
                    }
                  >
                    {serviceList.map((service) => (
                      <MenuItem
                        key={service.id?.toString()}
                        value={service.name}
                      >
                        {service.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {serviceList.length === 0 && canAddOwnService && (
                    <Button
                      variant="outlined"
                      onClick={() => setIsAddServiceDialogOpen(true)}
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: 44,
                        minHeight: 44,
                        flexShrink: 0,
                        fontFamily: 'GFS Didot',
                        fontWeight: 'bold',
                        color: '#dd8709',
                        borderColor: '#dd8709',
                        '&:hover': {
                          borderColor: '#b86e08',
                          color: '#b86e08',
                        },
                      }}
                    >
                      + Add Service
                    </Button>
                  )}
                </Box>

                {canAddOwnService && (
                  <AddServices
                    open={isAddServiceDialogOpen}
                    onClose={() => setIsAddServiceDialogOpen(false)}
                    hideTriggerButton
                    barberIdOverride={activeBarberId ?? undefined}
                    shopIdOverride={
                      role === UserRole.SHOP_BARBER ? shopId : undefined
                    }
                    onCreated={(service) => {
                      if (activeBarberId) {
                        loadBarberData(activeBarberId);
                      }
                      setFormData((prev) => ({
                        ...prev,
                        service: service.name ?? '',
                      }));
                    }}
                  />
                )}

                {editingSlot ? (
                  <TextField
                    select
                    label="Start Time"
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData({ ...formData, startTime: e.target.value })
                    }
                    fullWidth
                    disabled={loading}
                  >
                    {selectedDate &&
                      getTimeOptions(selectedDate).map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                  </TextField>
                ) : (
                  <FormControl
                    fullWidth
                    disabled={loading || !formData.service}
                  >
                    <InputLabel>Start Times</InputLabel>
                    <Select
                      multiple
                      value={multiStartTimes}
                      onChange={(e) =>
                        setMultiStartTimes(
                          typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : (e.target.value as string[])
                        )
                      }
                      input={<OutlinedInput label="Start Times" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {selectedDate &&
                        getAvailableStartTimesForDate(
                          selectedDate,
                          formData.service
                        ).map((time) => (
                          <MenuItem key={time} value={time}>
                            <Checkbox
                              checked={multiStartTimes.indexOf(time) > -1}
                            />
                            <ListItemText primary={time} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveBookingSlot}
              variant="contained"
              disabled={!canSave}
            >
              {loading
                ? 'Saving...'
                : editingSlot
                ? 'Update'
                : multiStartTimes.length > 1
                ? `Save ${multiStartTimes.length} Slots`
                : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isCopyDialogOpen}
          onClose={() => setIsCopyDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Copy Slots{' '}
            {selectedDate && `to ${format(selectedDate, 'MMMM d, yyyy')}`}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a source date to copy its active booking slots from.
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={copySourceDate}
                onChange={setCopySourceDate}
                disabled={copyLoading}
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  mx: 'auto',
                  '& .MuiPickersCalendarHeader-root': {
                    pl: 0.5,
                    pr: 0,
                  },
                  '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
                    justifyContent: 'space-around',
                  },
                  '& .MuiPickersDay-root': {
                    width: { xs: 30, sm: 36 },
                    height: { xs: 30, sm: 36 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  },
                }}
              />
            </LocalizationProvider>
            {copySourceDate && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {copySourceSlotCount > 0
                  ? `${copySourceSlotCount} active slot${
                      copySourceSlotCount === 1 ? '' : 's'
                    } found on ${format(copySourceDate, 'MMMM d, yyyy')}${
                      copySourceDateHours && !copySourceDateHours.open
                        ? ' (business closed that day)'
                        : ''
                    }.`
                  : `No active slots found on ${format(
                      copySourceDate,
                      'MMMM d, yyyy'
                    )}.`}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsCopyDialogOpen(false)}
              disabled={copyLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCopySlots}
              variant="contained"
              disabled={
                !copySourceDate || copySourceSlotCount === 0 || copyLoading
              }
            >
              {copyLoading ? 'Copying...' : 'Copy Slots'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!notification}
          autoHideDuration={6000}
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {notification ? (
            <Alert
              onClose={() => setNotification(null)}
              severity={notification.severity}
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ScheduleManager;
